/**
 * @layer/canonical-data/schema-primitives — shared JSON-Schema `pattern`
 * strings reused across canonical-action input schemas.
 *
 * These constants were previously copy-pasted into ~45 action files (each
 * redefining its own `const UUID_V7 = "…"` / `E164` / `EMAIL`). Centralising
 * them removes the duplication AND collapses the dual source-of-truth between
 * the schema strings here and the `UUID_V7_RE` brand check in `ids.ts`, which
 * derives its `RegExp` from `UUID_V7_PATTERN` (see ids.ts).
 *
 * IMPORTANT — these are JSON-Schema `pattern` *strings*, not `RegExp`. They
 * use double-escaped backslashes (`\\+`, `\\d`, `\\.`) so the emitted schema
 * is byte-identical to the historical per-file constants.
 */

/**
 * Lower-case UUID v7 (RFC 9562 §5.7). Byte-identical to the per-action
 * `UUID_V7` constants and to `new RegExp(UUID_V7_PATTERN)` === the historical
 * `UUID_V7_RE` brand regex in `ids.ts`.
 */
export const UUID_V7_PATTERN =
  "^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$";

/**
 * E.164 phone number: `+` followed by 1–15 digits, first digit 1–9.
 *
 * NOTE — the historical action files diverged here: most used `\d{1,14}`
 * (1–15 total digits, RFC-correct) but a minority used `\d{6,14}` (≥7 digits).
 * This module standardises on the RFC-correct `\d{1,14}` form; the migration
 * of the `\d{6,14}` outliers is a deliberate behaviour change (min digits
 * drops 7→1) and is called out in the CHANGELOG.
 */
export const E164_PATTERN = "^\\+[1-9]\\d{1,14}$";

/**
 * Lightweight email pattern — local-part chars + `@` + domain with at least
 * one dot and a 2+ char TLD. The pattern accepts mixed case at the boundary;
 * callers canonicalise with {@link normalizeEmail} before a `members.email`
 * write or a by-email lookup so one person never lands on two accounts.
 */
export const EMAIL_PATTERN =
  "^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$";

/**
 * Canonical email normalization — the SINGLE source of truth for turning a
 * user-supplied address into its stored / looked-up form. Trims surrounding
 * whitespace and lower-cases the whole address.
 *
 * Email is case-insensitive in every real-world provider; storing it verbatim
 * let `architektur@…` and `Architektur@…` create two distinct `members` rows
 * for one person (the `UNIQUE (studio_id, email)` constraint compares bytes,
 * not case-folded). EVERY canonical `members.email` WRITE and EVERY by-email
 * member LOOKUP must run the address through this helper — and lookups compare
 * `lower(email) = $1` so they also match rows written before this landed.
 *
 * Lower-casing the local part is technically stricter than RFC 5321 (which
 * leaves local-part case to the receiving MTA), but no consumer mail provider
 * distinguishes case, and collapsing it is the only way to guarantee one
 * account per human — the correct trade-off for this domain.
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Compiled {@link EMAIL_PATTERN}, for the runtime checks in this module. */
const EMAIL_RE = new RegExp(EMAIL_PATTERN);

/**
 * Canonical address EXTRACTION — turn a mail-header-shaped value into the bare
 * `addr-spec` a canonical boundary will accept, or `null` when it cannot be
 * read confidently. The email twin of {@link normalizeToE164}: a `null` (log
 * it, skip it) is always safer than dispatching something a schema is
 * guaranteed to reject.
 *
 * Handles the RFC 5322 `name-addr` form — `Display Name <addr@host>` — plus
 * surrounding whitespace. The address is taken from the LAST bracketed run so
 * a quoted display name containing `<` cannot shadow it. Anything that still
 * fails {@link EMAIL_PATTERN} afterwards (a comma-joined recipient list, an
 * unterminated bracket, a bare token with no `@`) returns `null` rather than a
 * guess.
 *
 * Deliberately does NOT lower-case: this exists for values echoed back by a
 * vendor, where the stored row is a record of what the vendor said and every
 * consumer already compares `lower(...)`. Call {@link normalizeEmail} on top
 * when the value is about to key a canonical identity.
 *
 * WHY this exists (the 2026-08-07 Resend webhook that could not land): the
 * Resend connector passed `data.to` through verbatim and guarded only against
 * an EMPTY value, so a non-empty but unusable address dispatched straight into
 * the chokepoint, came back `input_invalid`, and returned HTTP 400 to Svix —
 * which retried the same message eight times over 33 hours and would then have
 * dropped the event entirely. Emptiness was standing in for mappability; this
 * helper is the real predicate.
 */
export function extractEmailAddress(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const lastOpen = trimmed.lastIndexOf("<");
  const lastClose = trimmed.lastIndexOf(">");
  const candidate =
    lastOpen !== -1 && lastClose > lastOpen
      ? trimmed.slice(lastOpen + 1, lastClose).trim()
      : trimmed;
  return EMAIL_RE.test(candidate) ? candidate : null;
}

/**
 * Canonical phone normalization — the SINGLE source of truth for turning a
 * user-supplied phone number into its stored / looked-up E.164 form. Returns
 * `null` when the input cannot be confidently normalized (so callers store the
 * raw value / treat it as a no-match rather than guessing a wrong country and
 * mis-merging two humans onto one account).
 *
 * WHY this exists (the 2026-07-15 duplicate-member storm): `members.phone` had
 * no canonical form. A member who signed up on the website as `+44 7700 900881`
 * and later messaged on WhatsApp (whose Meta `wa_id` arrives as `447700900881`,
 * digits-only) landed on TWO rows — the website row keyed on the real email,
 * a fresh placeholder row keyed on `447700900881@whatsapp.placeholder.layer` —
 * because dedup only ran on `lower(email)`. Pausing the AI on one row left the
 * other unpaused, so the agent kept replying (the phantom-pause bug). Running
 * EVERY phone through this helper before any member creation / lookup collapses
 * those forms to one identity.
 *
 * Country default is **GB** — this is a UK studio (E-Pulsive Belgravia); a bare
 * national number (leading `0`, or a 10-digit `7…` mobile) is assumed UK. The
 * mapping, applied to the digit string (all non-digits stripped; a single
 * leading `+` remembered):
 *   - `+<digits>`                → `+<digits>`            (already international)
 *   - `00<digits>`               → `+<digits>`            (IDD 00 prefix → +)
 *   - `0<national>`   (GB trunk) → `+44<national>`        (`07700…` → `+447700…`)
 *   - `44<digits>`               → `+44<digits>`          (UK CC without the +)
 *   - 10-digit `7<9 digits>`     → `+447<9 digits>`       (UK mobile, no trunk 0)
 *   - other bare ≥11 digits      → `+<digits>`            (assume leading CC)
 *   - anything shorter / stranger → `null`                (too ambiguous → skip)
 * The result is validated against {@link E164_PATTERN}; a candidate that fails
 * (e.g. leading `0` after `+`, or >15 digits) returns `null`.
 *
 * This is deliberately UK-centric and intentionally conservative on the tail
 * cases — a `null` (skip dedup, store raw) is always safer than a confident
 * wrong guess that welds two different people together.
 */
export function normalizeToE164(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (digits === "") return null;

  let candidate: string;
  if (hasPlus) {
    candidate = `+${digits}`;
  } else if (digits.startsWith("00")) {
    candidate = `+${digits.slice(2)}`;
  } else if (digits.startsWith("0")) {
    // GB national trunk-0 → +44 (this is a UK studio).
    candidate = `+44${digits.slice(1)}`;
  } else if (digits.startsWith("44")) {
    // UK country code sent without the leading + (Meta WhatsApp wa_id form).
    candidate = `+${digits}`;
  } else if (digits.length === 10 && digits.startsWith("7")) {
    // UK mobile typed without the trunk 0 (e.g. "7700 900881").
    candidate = `+44${digits}`;
  } else if (digits.length >= 11) {
    // Already-internationally-formatted bare digits — assume the leading
    // digits ARE the country code.
    candidate = `+${digits}`;
  } else {
    // Too short / too ambiguous to assign a country safely — skip (no dedup).
    return null;
  }

  return new RegExp(E164_PATTERN).test(candidate) ? candidate : null;
}

/**
 * Canonical Instagram-handle normalization — the SINGLE source of truth for
 * turning a user-supplied @handle into its comparable form. Returns `null` when
 * the input isn't a usable handle, so callers skip the match rather than guess.
 *
 * WHY this exists — Instagram is the THIRD of the three identifiers that make a
 * member one human (email · phone · Instagram), and it was the only one with no
 * canonical form. `members.metadata.instagram_handle` is hand-typed by staff,
 * while every inbound routing table is keyed on the numeric IGSID, so the two
 * never met: on 2026-08-06 the first DM from a member whose handle was already
 * on their card minted a duplicate placeholder, and because the placeholder
 * holds no packages, `book_session` answered `no_credits` to someone with 11
 * credits. `member_self_signup` already collapsed the same duplicate for email
 * and for phone; this is the missing leg.
 *
 * The mapping (all case-folded — IG handles are case-insensitive):
 *   - `@name` / `name`                      → `name`     (leading @s stripped)
 *   - `instagram.com/name`, with any scheme,
 *     `www.`, trailing `/`, or `?query`     → `name`     (staff paste URLs)
 *   - anything failing IG's own charset
 *     (letters, digits, `.`, `_`; ≤30)      → `null`     (never a wrong match)
 *
 * Deliberately conservative in the same spirit as {@link normalizeToE164}: a
 * `null` (skip the match) is always safer than a confident wrong guess that
 * welds two different people onto one account.
 */
export function normalizeInstagramHandle(raw: string): string | null {
  let value = raw.trim().toLowerCase();
  if (value === "") return null;

  // Staff paste profile URLs as often as they type handles — collapse both.
  const urlMatch = /(?:^|\.|\/\/)instagram\.com\/([^/?#]+)/.exec(value);
  if (urlMatch?.[1] !== undefined) {
    value = urlMatch[1];
  }

  value = value.replace(/^@+/, "").trim();
  if (value === "") return null;

  // Instagram's own charset: letters, digits, period, underscore; max 30.
  return /^[a-z0-9._]{1,30}$/.test(value) ? value : null;
}

/**
 * Dial codes recognised by {@link formatPhoneForDisplay} — mirrors the
 * booking site's `COMMON_DIAL_CODES` (Hercie `lib/phone.ts`) so the chat
 * and the widget render the SAME confirm string for the same number.
 * Longest-match wins; an unlisted code falls back to the raw E.164.
 */
const DISPLAY_DIAL_CODES: ReadonlyArray<string> = [
  "+44", "+1", "+353", "+49", "+33", "+34", "+39", "+31", "+32", "+41",
  "+43", "+351", "+46", "+47", "+45", "+48", "+30", "+971", "+966", "+974",
  "+965", "+973", "+968", "+961", "+962", "+20", "+972", "+90", "+91",
  "+61", "+64", "+65", "+852", "+27", "+234", "+86", "+81", "+55", "+52",
  "+7",
];

/**
 * Human-friendly rendering of an E.164 for confirm/echo copy — byte-parity
 * with the booking site's `formatPhoneForDisplay` ("Is this your number?"):
 * UK groups its mobile as `+44 7700 900880`; any other recognised dial code
 * renders `<dial> <national>`; unknown codes show the raw E.164.
 */
export function formatPhoneForDisplay(e164: string): string {
  if (e164.startsWith("+44") && e164.length >= 12) {
    const rest = e164.slice(3);
    return `+44 ${rest.slice(0, 4)} ${rest.slice(4)}`;
  }
  const match = DISPLAY_DIAL_CODES.filter((dial) => e164.startsWith(dial)).sort(
    (a, b) => b.length - a.length,
  )[0];
  if (match !== undefined) return `${match} ${e164.slice(match.length)}`;
  return e164;
}

/**
 * Member-tag slug — lower-kebab machine id, 1..63 chars (SP-TAGS). The single
 * source of truth for what a tag slug IS: `add_member_tag` /
 * `remove_member_tag` input schemas AND the L2-YAML flow-definition tag
 * conditions (`entry_tag` / `exit_on_tag_added` / `exit_on_tag_removed`,
 * tags-taxonomy task) all validate against THIS pattern so the two layers can
 * never drift. Namespacing is by dash prefix (`source-whatsapp`,
 * `membership-active`) — colons are deliberately NOT in the grammar.
 */
export const TAG_SLUG_PATTERN = "^[a-z0-9][a-z0-9-]{0,62}$";

/**
 * Convenience `{ type: "string", pattern }` schema fragments for the common
 * case of a bare string field. `as const` keeps them readonly-literal so Ajv's
 * type inference and `additionalProperties` handling are unchanged.
 */
export const uuidV7StringSchema = {
  type: "string",
  pattern: UUID_V7_PATTERN,
} as const;

export const e164StringSchema = {
  type: "string",
  pattern: E164_PATTERN,
} as const;

export const emailStringSchema = {
  type: "string",
  pattern: EMAIL_PATTERN,
} as const;
